import { AxiosError } from "axios";
import {
  Button,
  Container,
  Form,
  Input,
  Message,
  Stack,
  Text,
  useBasisFormContext,
  useWatch,
  ValidationError,
} from "basis";
import { FormActions } from "components/FormActions/FormActions";
import { EnhancedAccount } from "library/accounts/types";
import { PutEmailResponse } from "library/api/email/types.generated";
import { putHref } from "library/api/httpMethods";
import { OtpRequiredErrorType } from "library/api/otp/otp";
import {
  validateEmailDifferentFrom,
  validateEmailSameAs,
} from "library/validation/customInputValidation";
import { AccountHeader } from "pages/ManagePersonalDetails/components/AccountHeader";
import { useNav } from "pages/ManagePersonalDetails/hooks/useNav";
import {
  Actions,
  ActionTypes,
  UpdateEmailFormValues,
} from "pages/ManagePersonalDetails/UpdateEmail/types";
import { Dispatch } from "react";
import { UseFormReturn } from "react-hook-form";
import { useMutation } from "react-query";
import { FullPersonalDetails, PersonalDetails } from "../types";

interface Props {
  account: EnhancedAccount;
  fullPersonalDetails: PersonalDetails;
  dispatch: Dispatch<Actions>;
  methods: UseFormReturn<UpdateEmailFormValues>;
  setError: (name: "email" | "confirmEmail", error: ValidationError) => void;
}

export const UpdateEmailForm = ({
  account,
  fullPersonalDetails,
  dispatch,
  methods,
  setError,
}: Props) => {
  const { goBack } = useNav();
  const { Field } = useBasisFormContext<UpdateEmailFormValues>();
  const { control } = methods;
  const email = useWatch({
    control,
    name: "email",
  });

  const updateEmailMutation = useMutation<
    { verifyUrl: string },
    AxiosError | AxiosError<OtpRequiredErrorType>,
    { newEmail: string }
  >(async ({ newEmail }) => {
    const res = await putHref<PutEmailResponse, { email: string }>(
      (fullPersonalDetails as FullPersonalDetails).updateEmailEndpoint,
      { email: newEmail },
    );

    const verifyUrl = res.data.links.find(
      (link) => link.rel === "update_email_verify",
    )?.href;
    if (!verifyUrl) {
      console.error("verify url should be provided");
      throw new window.Error("Verify Url missing");
    }
    return { verifyUrl };
  });

  const onSubmit = async (values: UpdateEmailFormValues) => {
    updateEmailMutation.mutate(
      { newEmail: values.email },
      {
        onSuccess({ verifyUrl }) {
          dispatch({ type: ActionTypes.VERIFY, verifyUrl });
        },
        onError(err) {
          if (err.response?.data.type === "VALIDATION_ERROR") {
            setError("email", "Invalid email");
            return;
          }
          if (err.response?.data.type !== "2FA_REQUIRED") {
            dispatch({ type: ActionTypes.ERROR });
            return;
          }
          const createSessionUrl = err.response?.data.links.find(
            (link) => link.rel === "create_otp_session",
          )?.href;
          if (!createSessionUrl || !account.credit_card?.token) {
            dispatch({ type: ActionTypes.ERROR });
            return;
          }
          dispatch({
            type: ActionTypes.OTP_NEEDED,
            createSessionUrl,
            token: account.credit_card?.token,
            email,
          });
        },
      },
    );
  };
  return (
    <Stack gap="8">
      <AccountHeader account={account} />
      <Stack gap="4">
        <Text as="h3" textStyle="heading5" color="primary.blue.t100">
          Email address
        </Text>
        <Text>{fullPersonalDetails.emailAddress}</Text>
      </Stack>
      <Message severity="info-or-minor" bg="secondary.lightBlue.t25">
        Please note this will only update your contact email. Your Latitude ID
        (the email used to log onto your app or LSC) will remain unchanged.
      </Message>
      <Form methods={methods} onSubmit={onSubmit}>
        <Container width="100%" width-sm="400">
          <Stack gap="8">
            <Field
              as={Input}
              label="New email address"
              name="email"
              type="email"
              validate={validateEmailDifferentFrom(
                fullPersonalDetails.emailAddress as string,
                "New email can't be same as the old one",
              )}
            />
            <Field
              as={Input}
              label="Confirm email address"
              name="confirmEmail"
              type="email"
              validate={validateEmailSameAs(
                email,
                "Must match the new email address above",
              )}
            />
            <FormActions>
              <FormActions.Primary>
                <Button type="submit" loading={updateEmailMutation.isLoading}>
                  Next
                </Button>
              </FormActions.Primary>
              <FormActions.Secondary>
                <Button variant="secondary" onClick={goBack}>
                  Back
                </Button>
              </FormActions.Secondary>
            </FormActions>
          </Stack>
        </Container>
      </Form>
    </Stack>
  );
};
